import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckIcon, HandIcon, LeafIcon, RotateCcwIcon, TruckIcon } from 'lucide-react';
import { productBenefits } from '../../data/productBenefits';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
import { getProduct, productPath } from '../../utils/products';
import type { Product } from '../../types/product';
import { Stars } from '../shop/Stars';
import { QuantityStepper } from '../shop/QuantityStepper';
const badgeLabel: Record<string, string> = {
  bestseller: 'Bestseller',
  nieuw: 'Nieuw',
  cadeau: 'Cadeau-tip'
};
export function BuyBox({
  product
}: {
  product: Product;
}) {
  const {
    add
  } = useCart();
  const navigate = useNavigate();
  const living = product.line === 'living';
  const [variantId, setVariantId] = useState(product.variants[0].id);
  const [qty, setQty] = useState(1);
  const variant = product.variants.find(v => v.id === variantId) ?? product.variants[0];
  const included = (product.includes ?? []).map(s => getProduct(s)).filter((p): p is Product => Boolean(p));
  const why = productBenefits[product.slug] ?? [];
  const legend = living ? 'Uitvoering' : /\d/.test(variant.label) ? 'Inhoud' : 'Variant';
  const primary = living ? 'rounded-lg bg-terracotta text-base font-semibold text-white hover:bg-terracotta-dark' : 'bg-ink text-sm font-semibold uppercase tracking-[0.14em] text-white hover:bg-pink';
  const secondary = living ? 'rounded-lg border border-line bg-white text-base font-semibold text-ink hover:border-ink' : 'border border-ink bg-white text-sm font-semibold uppercase tracking-[0.14em] text-ink hover:bg-ink hover:text-white';
  const buyNow = () => {
    add(product.slug, variant.id, qty, false);
    navigate('/afrekenen');
  };
  return <div data-id="element-404" data-mp="h228fssc6ekz">
      {product.badges.length > 0 && <div className="flex gap-2" data-id="element-405" data-mp="c4agwzvlsuhb">
          {product.badges.map(b => <span key={b} className={living ? 'rounded-md bg-majorelle px-2 py-1 text-xs font-semibold text-white' : 'bg-ink px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white'} data-id="element-406" data-mp="c6dh2cqh03zr">
              {badgeLabel[b]}
            </span>)}
        </div>}
      <h1 className={living ? 'mt-4 text-3xl font-semibold tracking-tight text-ink sm:text-4xl' : 'mt-4 font-display text-4xl uppercase tracking-display text-ink sm:text-5xl'} data-id="element-407" data-mp="3tn7vpt7is5f3">
        {product.name}
      </h1>
      <p className="mt-1.5 text-lg font-light text-muted" data-id="element-408" data-mp="q8270298retb">{product.subtitle}</p>
      <div className="mt-4" data-id="element-409" data-mp="bh31x17m78eh">
        <a href="#details" className="inline-block" data-id="element-410" data-mp="ggfzoh00xzvu">
          <Stars rating={product.rating} reviews={product.reviews} size="md" data-id="element-411" data-mp="gf4ss8ba8c0i" />
        </a>
      </div>

      <p className="mt-6 flex items-baseline gap-3" data-id="element-412" data-mp="144fovchoti8b">
        <span className="text-3xl font-semibold tracking-tight tabular-nums" data-id="element-413" data-mp="18wgysrrduwv6">{formatPrice(variant.price * qty)}</span>
        {variant.compareAt && <span className="text-lg text-muted line-through" data-id="element-414" data-mp="2tlupej09gcbf">{formatPrice(variant.compareAt * qty)}</span>}
        {qty > 1 && <span className="text-sm text-muted" data-id="element-415" data-mp="2onsy5asol6ov">{qty} × {formatPrice(variant.price)}</span>}
      </p>
      <p className="mt-4 text-lg font-light leading-relaxed text-ink" data-id="element-416" data-mp="32eo8ysmqpxv3">{product.benefit}</p>
      {why.length > 0 && <ul className="mt-5 space-y-2.5" aria-label="Waarom je dit wilt" data-id="element-417" data-mp="2yg8g483z9v9r">
          {why.map(w => <li key={w.title} className="flex items-start gap-3 text-[15px]" data-id="element-418" data-mp="25p6upp9oz3hs">
              <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${living ? 'bg-majorelle/10 text-majorelle' : 'bg-blush text-berry'}`} data-id="element-419" data-mp="25r9uv24k6d08">
                <CheckIcon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" data-id="element-420" data-mp="2eotvq9yie7ab" />
              </span>
              {w.title}
            </li>)}
        </ul>}

      {product.variants.length > 1 && <fieldset className="mt-8" data-id="element-421" data-mp="3q4io5guj7yii">
          <legend className="text-sm font-medium" data-id="element-422" data-mp="p9udt9tvoptu">
            {legend}: <span className="text-muted" data-id="element-423" data-mp="pbdw15z3hay9">{variant.label}</span>
          </legend>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-424" data-mp="u9p2r7u3f5c9">
            {product.variants.map(v => {
          const active = v.id === variant.id;
          return <button key={v.id} type="button" role="radio" aria-checked={active} onClick={() => setVariantId(v.id)} className={`flex flex-col items-start border px-4 py-3 text-left transition-colors duration-150 ${living ? 'rounded-lg' : ''} ${active ? 'border-ink bg-white ring-1 ring-ink' : 'border-line bg-white hover:border-muted'}`} data-id="element-425" data-mp="1484th9p7kcl8">
                  <span className="text-sm font-semibold" data-id="element-426" data-mp="1rxz5jp1pdx4f">{v.label}</span>
                  <span className="text-sm text-muted" data-id="element-427" data-mp="1vxrisf21krg4">{formatPrice(v.price)}</span>
                </button>;
        })}
          </div>
        </fieldset>}

      {included.length > 0 && <div className="mt-8" data-id="element-428" data-mp="3cegp2mb0637m">
          <p className="text-sm font-medium" data-id="element-429" data-mp="3cdf3hnwd1ybn">In deze set</p>
          <ul className="mt-3 flex flex-wrap gap-2" data-id="element-430" data-mp="37eu41qoxnio9">
            {included.map(p => <li key={p.slug} data-id="element-431" data-mp="32h2aytcjxqih">
                <Link to={productPath(p)} className="flex items-center gap-2 rounded-full border border-line bg-white py-1 pl-1 pr-3 text-sm hover:border-ink" data-id="element-432" data-mp="32hu5e0uexi76">
                  <img src={p.image} alt="" className="h-7 w-7 rounded-full object-cover" data-id="element-433" data-mp="3v4oey2poyx5v" />
                  {p.name}
                </Link>
              </li>)}
          </ul>
        </div>}

      <div className="mt-8 flex gap-3" data-id="element-434" data-mp="3lg6j7zw9rg93">
        <QuantityStepper value={qty} onChange={setQty} size="lg" data-id="element-435" data-mp="3lf4vovy2h1ah" />
        <button type="button" onClick={() => add(product.slug, variant.id, qty)} className={`h-14 flex-1 whitespace-nowrap px-6 transition-colors duration-150 ${primary}`} data-id="element-436" data-mp="mqg52buy8v31">
          In winkelwagen
        </button>
      </div>
      <button type="button" onClick={buyNow} className={`mt-3 h-14 w-full whitespace-nowrap transition-colors duration-150 ${secondary}`} data-id="element-437" data-mp="2g83witsu13nv">
        Direct afrekenen
      </button>

      <p className="mt-5 flex items-center gap-2 text-sm" data-id="element-438" data-mp="22emexejpi10h">
        <span className="h-2 w-2 rounded-full bg-olive" aria-hidden="true" data-id="element-439" data-mp="27es2aiiy11cr" />
        <span data-id="element-440" data-mp="1si18qpvyajv2">
          <strong className="font-medium text-olive" data-id="element-441" data-mp="1shhq16yh7rfz">Op voorraad</strong> — vóór 22:00 besteld, morgen in huis
        </span>
      </p>

      <ul className="mt-8 grid gap-3 border-t border-line pt-6 text-sm text-muted" data-id="element-442" data-mp="1xix94th3fnvq">
        <li className="flex items-center gap-3" data-id="element-443" data-mp="1jjfjyczvy8lu"><TruckIcon className="h-4 w-4 text-ink" data-id="element-444" data-mp="1ogxvmb5af1xj" /> Gratis verzending vanaf € 50</li>
        <li className="flex items-center gap-3" data-id="element-445" data-mp="1oj1ycprg9uw8"><RotateCcwIcon className="h-4 w-4 text-ink" data-id="element-446" data-mp="3dvcbs728pghf" /> 30 dagen bedenktijd</li>
        {living ? <li className="flex items-center gap-3" data-id="element-447" data-mp="3iv8johe25s29"><HandIcon className="h-4 w-4 text-ink" data-id="element-448" data-mp="3iu77uap7x1jh" /> Handgemaakt in Marokko — elk stuk is uniek</li> : <li className="flex items-center gap-3" data-id="element-449" data-mp="3swt0tk7v8ggk"><LeafIcon className="h-4 w-4 text-ink" data-id="element-450" data-mp="3o0cb4ll0pu1d" /> 100% puur, zonder toevoegingen</li>}
      </ul>

      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-line bg-white/95 px-5 py-3 backdrop-blur lg:hidden" data-id="element-451" data-mp="5m32q1ju559r">
        <div className="min-w-0 flex-1" data-id="element-452" data-mp="t06tmrl5jqe">
          <p className="truncate text-sm font-medium" data-id="element-453" data-mp="fjhidyxxpq10">{product.name} · {variant.label}</p>
          <p className="text-sm font-semibold tabular-nums" data-id="element-454" data-mp="al685n3q9di9">{formatPrice(variant.price * qty)}</p>
        </div>
        <button type="button" onClick={() => add(product.slug, variant.id, qty)} className={`h-12 whitespace-nowrap px-6 transition-colors duration-150 ${primary}`} data-id="element-455" data-mp="ol79f3nwmmy9">
          In winkelwagen
        </button>
      </div>
    </div>;
}